import { UniqueUserId, UserClickFields, ViewContentFields } from '@xylabs/pixel'
import {
  FacebookAddToCart,
  FacebookCartProperties,
  FacebookContentProperties,
  FacebookContentsItem,
  FacebookInitiateCheckout,
  FacebookValueProperties,
  FacebookViewContent,
} from '@xylabs/react-pixel'
import { delay } from '@xylabs/sdk-js'
import { sha256 } from 'js-sha256'

import { Cart, CartItemSlot } from 'business-logic/cart/hooks'
import { AddressInfoData } from 'business-logic/cart/models'
import { ContactInfoWrapper } from 'business-logic/cart/wrappers'
import { CoinUserEventHandlerBase } from './CoinUserEventHandlerBase'
import { CustomProperties } from './CustomProperties'
import { FacebookAddressInfoComplete, FacebookContactInfoComplete, FacebookEvents } from './Facebook'
import { TestData } from './TestData'

export class FacebookEventHandler<T> extends CoinUserEventHandlerBase<T> {
  async emailCollected(fields: { email: string }) {
    await this.events.emailCollected().send(this.buildCustomProperties(fields), this.eventId)
  }

  async accountCreated() {
    await this.events.accountCreated().send(this.buildCustomProperties(), this.eventId)
  }

  async shareCompleted() {
    await this.events.shareCompleted().send(this.buildCustomProperties(), this.eventId)
  }

  async buyticketsCompleted() {
    await this.events.buyticketsCompleted().send(this.buildCustomProperties(), this.eventId)
  }

  async installCompleted() {
    await this.events.installCompleted().send(this.buildCustomProperties(), this.eventId)
  }

  async funnelCompleted() {
    await this.events.funnelCompleted().send(this.buildCustomProperties(), this.eventId)
  }

  async offerRequested(fields: { id: string }) {
    await this.events.offerRequested().send(this.buildCustomProperties(fields), this.eventId)
  }

  async feedbackScoreCollected(fields: { score: string | number }) {
    await this.events.feedbackScoreCollected().send(this.buildCustomProperties(fields), this.eventId)
  }

  async feedbackSourceCollected(fields: { source: string }) {
    await this.events.feedbackSourceCollected().send(this.buildCustomProperties(fields), this.eventId)
  }

  async goodFeedbackRequested() {
    await this.events.goodFeedbackRequested().send(this.buildCustomProperties(), this.eventId)
  }

  async badFeedbackRequested() {
    await this.events.badFeedbackRequested().send(this.buildCustomProperties(), this.eventId)
  }

  private events = new FacebookEvents<CustomProperties>()

  async contactInfoComplete() {
    const contactInfo = new ContactInfoWrapper().load()
    const data = contactInfo.getSanitizedData()
    await this.events.contactInfoComplete().send(this.buildCustomProperties<FacebookContactInfoComplete>(data), this.eventId)
  }

  async addressInfoComplete(addressInfo?: AddressInfoData) {
    await this.events.addressInfoComplete().send(this.buildCustomProperties<FacebookAddressInfoComplete>({ ...addressInfo }), this.eventId)
  }

  async paymentInfoComplete() {
    await this.events.paymentInfoComplete().send(this.buildCustomProperties(), this.eventId)
  }

  async addToCart() {
    await this.events.addToCart().send(this.buildCartProperties<FacebookAddToCart>(), this.eventId)
  }

  async purchaseCompleted() {
    await Promise.allSettled([
      this.events.purchase().send(this.buildValueProperties(), this.eventId),
      this.events.transactionCompleted().send(this.buildValueProperties(), this.eventId),
    ])
  }

  async userClick(fields: UserClickFields) {
    await this.events.userClick().send(this.buildCustomProperties(fields), this.eventId)
  }

  async initiateCheckout() {
    await this.events.initiateCheckout().send(this.buildCartProperties<FacebookInitiateCheckout>(), this.eventId)
  }

  async purchaseAttempted() {
    await this.events.transactionAttempted().send(this.buildCustomProperties(), this.eventId)
  }

  async purchaseFailed() {
    await this.events.transactionFailed().send(this.buildCustomProperties(), this.eventId)
  }

  async upsellViewed() {
    await this.events.upsellViewed().send(this.buildCustomProperties(), this.eventId)
  }

  async upsellUnavailable() {
    await this.events.upsellUnavailable().send(this.buildCustomProperties(), this.eventId)
  }

  async upsellAccepted() {
    await this.events.upsellAccepted().send(this.buildCustomProperties(), this.eventId)
  }

  async upsellSucceeded() {
    await this.events.upsellSucceeded().send(this.buildCustomProperties(), this.eventId)
  }

  async trialStarted() {
    await Promise.allSettled([
      this.events.startTrial().send(this.buildCartProperties(), this.eventId),
      this.events.trialStarted().send(this.buildCustomProperties(), this.eventId),
    ])
  }

  async upsellFailed() {
    await this.events.upsellFailed().send(this.buildCustomProperties(), this.eventId)
  }

  async viewContent(fields: ViewContentFields) {
    await this.events.viewContent().send(this.buildCustomProperties<FacebookViewContent>({ content_category: fields.name }), this.eventId)
  }

  async testStarted() {
    await this.events.testStarted().send(this.buildCustomProperties(), this.eventId)
  }

  async funnelStarted() {
    await this.events.funnelStarted().send(this.buildCustomProperties(), this.eventId)
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async customEvent(name: string, data: T) {
    await delay(0)
  }

  buildCartProperties<T extends FacebookCartProperties>(data?: T): T | FacebookCartProperties {
    const content_ids: string[] = []
    const contents: FacebookContentsItem[] = []

    const cart = Cart.get()
    for (const item of cart?.getItems() ?? []) {
      content_ids.push(item.planId)
      contents.push({
        id: item.planId,
        quantity: 1,
      })
    }

    return {
      ...data,
      ...this.buildValueProperties<T>(data),
      ...this.buildContentProperties<T>(data),
      content_ids,
      contents,
      num_items: content_ids.length,
    }
  }

  buildUpsellProperties<T extends FacebookCartProperties>(data?: T): T | FacebookCartProperties {
    const content_ids: string[] = []
    const contents: FacebookContentsItem[] = []

    const item = Cart.get().getItemsBySlot(CartItemSlot.Upsell)
    if (item) {
      content_ids.push(item.planId)
      contents.push({
        id: item.planId,
        quantity: 1,
      })
    }

    return {
      ...data,
      ...this.buildContentProperties<T>(data),
      content_ids,
      contents,
      currency: 'USD',
      num_items: content_ids.length,
      value: item?.value ?? 0,
    }
  }

  buildContentProperties<T extends FacebookContentProperties>(data?: T): T | FacebookContentProperties {
    const cart = Cart.get()
    return {
      ...data,
      ...this.buildCustomProperties<T>(data),
      content_name: cart.getItemSet(),
      content_type: 'product',
    }
  }

  buildValueProperties<T extends FacebookValueProperties>(data?: T): T | FacebookValueProperties {
    const cart = Cart.get()
    return {
      ...data,
      ...this.buildCustomProperties<T>(data),
      currency: 'USD',
      value: cart.getValue(),
    }
  }

  buildCustomProperties<T>(data?: T): T | CustomProperties {
    const cart = Cart.get()
    const coinId = new UniqueUserId().id
    const testData = new TestData().data
    return {
      ...data,
      coinId,
      external_ID: [coinId],
      external_id: [coinId],
      itemSet: cart.getItemSet(),
      testData,
      ...this.hashedUserData(),
    }
  }
  hashedUserData() {
    // https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/customer-information-parameters
    const contactInfo = new ContactInfoWrapper().load().data
    const em = contactInfo.email ? sha256(contactInfo.email) : undefined
    const fn = contactInfo.firstName ? sha256(contactInfo.firstName) : undefined
    const ln = contactInfo.lastName ? sha256(contactInfo.lastName) : undefined
    const ph = contactInfo.phone ? sha256(contactInfo.phone) : undefined
    return {
      em,
      fn,
      ln,
      ph,
    }
  }
}
